import { useEffect, useRef, useState } from "react";
import ReactConfetti from "react-confetti";
import { Container } from "react-bootstrap";
import { Text, Typeahead, Card, Badge, SkeletonLoader } from "@merge-api/merge-javascript-shared";
import {
  AdminCategory,
  ADMIN_CATEGORIES,
  displayNameForAPICategory,
} from "../../models/Categories";
import { useParams, useLocation, useHistory, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Info, Trophy } from "lucide-react";
import queryString from "query-string";
import { fetchWithAuth } from "../../api-client/api_client";
import { Integration } from "../../models/Entities";
import MergeTable from "../shared/MergeTable";
import { IntegrationAvatar } from "../shared/MergeAvatars";
import { CODE_BLOCK_PROGRESS_PATH, getBlueprintEditorPath } from "../../router/RouterUtils";
import {
  BlueprintProgress,
  BlueprintResult,
  AggregateStatCards,
  ErrorBanner,
  barClassForCategory,
  useSortState,
  SortTh,
} from "./codeBlockProgressShared";

const CodeBlockProgressIntegrationPage = () => {
  const { integrationId } = useParams<{ integrationId: string }>();
  const location = useLocation();
  const history = useHistory();

  const categoryFromUrl = queryString.parse(location.search).category as AdminCategory | undefined;

  const [integration, setIntegration] = useState<Integration | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<AdminCategory | null>(
    categoryFromUrl ?? null
  );
  const [blueprintResult, setBlueprintResult] = useState<BlueprintResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confettiPieces, setConfettiPieces] = useState(0);
  const confettiTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isFullyConverted =
    blueprintResult !== null &&
    blueprintResult.total_fetch_blueprints > 0 &&
    blueprintResult.blueprints_fully_converted === blueprintResult.total_fetch_blueprints;

  useEffect(() => {
    if (confettiTimer.current) clearTimeout(confettiTimer.current);
    if (isFullyConverted) {
      setConfettiPieces(350);
      confettiTimer.current = setTimeout(() => setConfettiPieces(0), 3500);
    } else {
      setConfettiPieces(0);
    }
    return () => {
      if (confettiTimer.current) clearTimeout(confettiTimer.current);
    };
  }, [blueprintResult]);

  // Fetch integration metadata; auto-select first category alphabetically if none is set
  useEffect(() => {
    fetchWithAuth({
      path: `/integrations/${integrationId}`,
      method: "GET",
      onResponse: (data: Integration) => {
        setIntegration(data);
        if (!selectedCategory && data.categories.length > 0) {
          const first = [...data.categories].sort((a, b) =>
            displayNameForAPICategory(a).localeCompare(displayNameForAPICategory(b))
          )[0];
          handleCategoryChange(first);
        }
      },
      onError: () => {},
    });
  }, [integrationId]);

  // Fetch blueprint data whenever integrationId or category changes
  useEffect(() => {
    setIsLoading(true);
    setBlueprintResult(null);
    setError(null);
    fetchWithAuth({
      path: `/integrations/${integrationId}/code-block-conversion-progress${
        selectedCategory ? `?category=${selectedCategory}` : ""
      }`,
      method: "GET",
      onResponse: (data: BlueprintResult) => {
        setBlueprintResult(data);
        setIsLoading(false);
      },
      onError: (err: Response | undefined) => {
        setError(err ? `Request failed: ${err.status} ${err.statusText}` : "Request failed");
        setIsLoading(false);
      },
    });
  }, [integrationId, selectedCategory]);

  const handleCategoryChange = (category: AdminCategory | null) => {
    setSelectedCategory(category);
    history.replace({
      pathname: location.pathname,
      search: category ? `?category=${category}` : "",
    });
  };

  type BlueprintSortCol =
    | "name"
    | "status"
    | "code_block_count"
    | "num_api_requests"
    | "num_api_request_loops"
    | "num_model_writes";

  const statusRank = (bp: BlueprintProgress) =>
    bp.is_fully_converted ? 2 : bp.has_code_block ? 1 : 0;

  const { sortCol: bSortCol, sortDir: bSortDir, handleSort: bHandleSort } = useSortState<
    BlueprintSortCol
  >("status");

  const sortedBlueprints: BlueprintProgress[] = blueprintResult
    ? [...blueprintResult.blueprints].sort((a, b) => {
        let cmp: number;
        if (bSortCol === "name") cmp = a.name.localeCompare(b.name);
        else if (bSortCol === "status") cmp = statusRank(a) - statusRank(b);
        else if (bSortCol === "code_block_count")
          cmp = a.code_block_names.length - b.code_block_names.length;
        else cmp = a[bSortCol] - b[bSortCol];
        return bSortDir === "asc" ? cmp : -cmp;
      })
    : [];

  const integrationImage = integration?.square_image ?? undefined;

  return (
    <Container fluid className="pb-8 px-8 pt-16">
      {confettiPieces > 0 && (
        <ReactConfetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={confettiPieces}
          recycle={false}
          gravity={0.18}
          colors={["#4FA853", "#2F7FC2", "#8C7BA5", "#E5A947", "#C94432", "#FF6E06"]}
          style={{ position: "fixed", top: 0, left: 0, zIndex: 9999, pointerEvents: "none" }}
        />
      )}
      {/* Back link */}
      <Link
        to={
          selectedCategory
            ? `${CODE_BLOCK_PROGRESS_PATH}?category=${selectedCategory}`
            : CODE_BLOCK_PROGRESS_PATH
        }
        className="inline-flex items-center gap-1 text-sm text-gray-40 hover:text-gray-70 mb-6"
      >
        <ArrowLeft size={14} />
        Code block progress
      </Link>

      {/* Header */}
      <div className="flex items-center justify-between mt-4 mb-6">
        <div className="flex items-center gap-3">
          {integration ? (
            <>
              <IntegrationAvatar
                integrationName={integration.name}
                squareImage={integrationImage}
              />
              <Text variant="h2" className="text-gray-70">
                {integration.name}
              </Text>
            </>
          ) : (
            <>
              <SkeletonLoader height={36} width={36} borderRadius={8} />
              <SkeletonLoader height={32} width={220} borderRadius={6} />
            </>
          )}
        </div>

        {/* Category filter */}
        <div className="flex items-center gap-2">
          <Text variant="sm" className="text-gray-40 whitespace-nowrap">
            Filter by category:
          </Text>
          <div className="w-44">
            <Typeahead
              options={integration?.categories.length ? integration.categories : ADMIN_CATEGORIES}
              getOptionLabel={(cat: AdminCategory) => displayNameForAPICategory(cat)}
              value={selectedCategory}
              onChange={(_event, value) => handleCategoryChange(value as AdminCategory | null)}
              placeholder="All categories"
            />
          </div>
        </div>
      </div>

      <div className="mt-4 mb-4 bg-gray-20 h-px" />

      <div className="mb-6 inline-flex items-center gap-2 rounded-md bg-blue-0 border border-blue-20 px-3 py-2 text-sm text-blue-60">
        <Info size={14} className="shrink-0" />
        Metrics cover fetch blueprints only.
      </div>

      {error && <ErrorBanner message={error} />}

      {isLoading && (
        <>
          {/* Skeleton stat cards */}
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-4 mb-8 mt-6">
            {[...Array(6)].map((_, i) => (
              <Card
                key={i}
                variant="outline"
                className="px-5 py-4 flex flex-col gap-3 border-gray-20"
              >
                <SkeletonLoader height={10} width={120} borderRadius={4} />
                <SkeletonLoader height={28} width={60} borderRadius={4} />
                <SkeletonLoader height={10} width={140} borderRadius={4} />
                <SkeletonLoader height={8} width={240} borderRadius={4} />
              </Card>
            ))}
          </div>

          {/* Skeleton table */}
          <SkeletonLoader height={16} width={100} borderRadius={4} />
          <Card variant="outline" className="p-0 border-gray-20 overflow-hidden mt-3">
            <div className="px-6 py-4 border-b border-gray-10 flex gap-8">
              {[160, 100, 100, 70, 60, 90].map((w, i) => (
                <SkeletonLoader key={i} height={12} width={w} borderRadius={4} />
              ))}
            </div>
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="px-6 py-4 border-b border-gray-10 last:border-b-0 flex items-center gap-8"
              >
                <SkeletonLoader height={13} width={160} borderRadius={4} />
                <SkeletonLoader height={22} width={100} borderRadius={12} />
                {[100, 70, 60, 90].map((w, j) => (
                  <SkeletonLoader key={j} height={13} width={w} borderRadius={4} />
                ))}
              </div>
            ))}
          </Card>
        </>
      )}

      {blueprintResult && (
        <>
          {isFullyConverted && (
            <div className="mt-6 mb-2 flex items-center gap-4 rounded-lg border border-green-20 bg-green-0 px-6 py-4">
              <Trophy size={28} className="text-green-50 shrink-0 animate-bounce" />
              <div>
                <Text variant="h4" className="text-green-60 leading-snug">
                  Fully converted!
                </Text>
                <Text variant="md" className="text-green-50 mt-0.5">
                  Every fetch blueprint in this category has been converted. Nice work.
                </Text>
              </div>
            </div>
          )}
          <div className="mt-6">
            <AggregateStatCards
              m={blueprintResult}
              accentClass={selectedCategory ? barClassForCategory(selectedCategory) : undefined}
            />
          </div>

          <Text variant="h4" className="mb-3 text-gray-70">
            Blueprints
          </Text>
          <Card variant="outline" className="p-0 border-gray-20">
            <MergeTable
              hover={false}
              hasMarginBottom={false}
              hasMorePaddingOnFirstElement
              hasColumnTitlesClickable
              header={
                <>
                  <SortTh col="name" sortCol={bSortCol} sortDir={bSortDir} onSort={bHandleSort}>
                    Name
                  </SortTh>
                  <SortTh col="status" sortCol={bSortCol} sortDir={bSortDir} onSort={bHandleSort}>
                    Status
                  </SortTh>
                  <SortTh
                    col="code_block_count"
                    sortCol={bSortCol}
                    sortDir={bSortDir}
                    onSort={bHandleSort}
                  >
                    Code blocks
                  </SortTh>
                  <SortTh
                    col="num_api_requests"
                    sortCol={bSortCol}
                    sortDir={bSortDir}
                    onSort={bHandleSort}
                  >
                    API req.
                  </SortTh>
                  <SortTh
                    col="num_api_request_loops"
                    sortCol={bSortCol}
                    sortDir={bSortDir}
                    onSort={bHandleSort}
                  >
                    Loops
                  </SortTh>
                  <SortTh
                    col="num_model_writes"
                    sortCol={bSortCol}
                    sortDir={bSortDir}
                    onSort={bHandleSort}
                  >
                    Model writes
                  </SortTh>
                </>
              }
              content={
                <>
                  {sortedBlueprints.map((bp) => (
                    <tr key={bp.id}>
                      <td>
                        <a
                          href={getBlueprintEditorPath(integrationId, bp.version_id)}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 text-base font-medium text-blue-60 hover:text-blue-70"
                        >
                          {bp.name}
                          <ArrowUpRight size={12} className="shrink-0" />
                        </a>
                      </td>
                      <td>
                        {bp.is_fully_converted ? (
                          <Badge color="green" size="sm">
                            Fully converted
                          </Badge>
                        ) : bp.has_code_block ? (
                          <Badge color="blue" size="sm">
                            Has code block
                          </Badge>
                        ) : (
                          <Badge color="gray" size="sm">
                            No code block
                          </Badge>
                        )}
                      </td>
                      <td className="text-base text-gray-50">
                        {bp.code_block_names.length > 0 ? (
                          bp.code_block_names.join(", ")
                        ) : (
                          <span className="text-gray-30">—</span>
                        )}
                      </td>
                      <td className="text-base tabular-nums text-center text-gray-60">
                        {bp.num_api_requests}
                      </td>
                      <td className="text-base tabular-nums text-center text-gray-60">
                        {bp.num_api_request_loops}
                      </td>
                      <td className="text-base tabular-nums text-center text-gray-60">
                        {bp.num_model_writes}
                      </td>
                    </tr>
                  ))}
                </>
              }
            />
          </Card>
        </>
      )}
    </Container>
  );
};

export default CodeBlockProgressIntegrationPage;
